import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, Page } from '@/components/Page';
import { api } from '@/lib/api';

interface Member {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  avatar_url: string | null;
  created_at: string;
}

interface Invite {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expires_at: string;
  created_at: string;
}

export function TeamPage() {
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: () => api<Member[]>('/v1/members') });
  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api<Invite[]>('/v1/members/invites'),
  });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');

  const invite = useMutation({
    mutationFn: (body: { email: string; role: string }) =>
      api('/v1/members/invites', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (_data, vars) => {
      setEmail('');
      qc.invalidateQueries({ queryKey: ['invites'] });
      toast.success(`Invite sent to ${vars.email}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to send invite');
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/members/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] });
      toast.success('Invite revoked');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke invite');
    },
  });

  function onInvite(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    invite.mutate({ email, role });
  }

  return (
    <Page title="Team" subtitle="Invite members and assign roles.">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Invite a teammate</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onInvite} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@example.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-role">Role</Label>
                <select
                  id="invite-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <Button type="submit" disabled={invite.isPending} className="w-full">
                {invite.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6 md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Members</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {members.isLoading ? (
                <Skeleton className="m-4 h-20" />
              ) : !members.data || members.data.length === 0 ? (
                <EmptyState title="No members yet" />
              ) : (
                <div className="divide-y">
                  {members.data.map((m) => (
                    <div key={m.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <div className="font-medium">{m.email}</div>
                        <div className="text-xs capitalize text-muted-foreground">{m.role}</div>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {m.role}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {invites.data && invites.data.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pending invites</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {invites.data.map((i) => (
                    <div key={i.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <div className="font-medium">{i.email}</div>
                        <div className="text-xs text-muted-foreground">
                          {i.role} · {i.status} · expires{' '}
                          {new Date(i.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      {i.status === 'pending' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => revoke.mutate(i.id)}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Badge variant="outline">{i.status}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </Page>
  );
}
